import { DialectModifier } from '@/types';

export const DIALECT_MODIFIERS: DialectModifier[] = [
  {
    id: 'southern-drawl',
    label: 'Southern Drawl',
    region: 'Deep South',
    description: 'Slow, honeyed vowel elongation with lazy consonant endings. Words drip like molasses off a porch railing.',
    traitModifiers: { grit: 5, rasp: 3, speed: -10, airiness: 8, lowEnd: 5 },
    physicsPrompt: `{DIALECT-PHYSICS: SOUTHERN-DRAWL}
[Dialect_Behavior: Elongated diphthongs; Soft consonant termination; Lazy glottal transitions]
[Dialect_Phrasing: Words stretch 20-30% longer; Natural drawl on open vowels]
[Dialect_Character: Honeyed warmth; Front-porch intimacy; Unhurried delivery]`,
    sunoTags: ['southern drawl', 'deep south vocals', 'drawling voice'],
    excludeTags: ['crisp enunciation', 'fast-paced', 'clipped delivery']
  },
  {
    id: 'appalachian-twang',
    label: 'Appalachian Twang',
    region: 'Appalachia',
    description: 'Nasal mountain twang with tight vowels and sharp consonant attacks. Fiddle-string tension in every syllable.',
    traitModifiers: { nasality: 15, grit: 8, projection: 5, speed: 5, airiness: -5 },
    physicsPrompt: `{DIALECT-PHYSICS: APPALACHIAN-TWANG}
[Dialect_Behavior: Nasal resonance emphasis; Tight vowel formation; Sharp consonant onset]
[Dialect_Phrasing: Clipped phrases with mountain-ridge rhythmic bounce]
[Dialect_Character: Fiddle-string vocal tension; Hollar-carry projection; Ridge-top clarity]`,
    sunoTags: ['appalachian twang', 'mountain vocals', 'nasal twang'],
    excludeTags: ['smooth', 'polished', 'urban']
  },
  {
    id: 'texas-grit',
    label: 'Texas Grit',
    region: 'Texas',
    description: 'Big, open vowels with dusty grit coating. Everything sounds like it crossed 500 miles of desert to get here.',
    traitModifiers: { grit: 12, projection: 10, lowEnd: 8, rasp: 5, cleanness: -8 },
    physicsPrompt: `{DIALECT-PHYSICS: TEXAS-GRIT}
[Dialect_Behavior: Wide-open vowel placement; Dusty rasp coating; Big chest resonance]
[Dialect_Phrasing: Confident, declarative phrasing; No word is wasted]
[Dialect_Character: Desert-mile authority; Sun-baked vocal cords; Wide-open space projection]`,
    sunoTags: ['texas grit', 'texas vocals', 'dusty voice', 'big texas sound'],
    excludeTags: ['delicate', 'whisper', 'ethereal']
  },
  {
    id: 'cajun-bayou',
    label: 'Cajun Bayou',
    region: 'Louisiana Bayou',
    description: 'French-Creole inflection with swampy warmth. Syllables slide into each other like water through cypress roots.',
    traitModifiers: { airiness: 10, speed: -5, vibrato: 8, nasality: 5, grit: 3 },
    physicsPrompt: `{DIALECT-PHYSICS: CAJUN-BAYOU}
[Dialect_Behavior: French-Creole vowel slides; Connected syllable flow; Swamp-water legato]
[Dialect_Phrasing: Words blur together; Musical rise-and-fall inflection]
[Dialect_Character: Bayou humidity warmth; Accordion-pump breath rhythm; Cypress-root depth]`,
    sunoTags: ['cajun vocals', 'bayou voice', 'creole inflection', 'swamp rock vocals'],
    excludeTags: ['crisp', 'angular', 'staccato']
  },
  {
    id: 'memphis-soul',
    label: 'Memphis Soul',
    region: 'Memphis, TN',
    description: 'Beale Street soul inflection with churchy melisma and gritty emotional weight. Every note testifies.',
    traitModifiers: { vibrato: 12, projection: 8, grit: 5, lowEnd: 5, cleanness: -3 },
    physicsPrompt: `{DIALECT-PHYSICS: MEMPHIS-SOUL}
[Dialect_Behavior: Soul-churned melismatic runs; Gospel-trained vibrato; Beale Street emotional weight]
[Dialect_Phrasing: Testimony-style delivery; Call-and-response breath patterns]
[Dialect_Character: Stax Records warmth; Church-raised power; Blues-born emotional truth]`,
    sunoTags: ['memphis soul', 'soul vocals', 'gospel-tinged', 'beale street'],
    excludeTags: ['cold', 'mechanical', 'monotone']
  },
  {
    id: 'nashville-polish',
    label: 'Nashville Polish',
    region: 'Nashville, TN',
    description: 'Music Row precision with just enough twang to stay country. Studio-refined but never sterile.',
    traitModifiers: { cleanness: 10, projection: 5, vibrato: 5, nasality: 3, grit: -5 },
    physicsPrompt: `{DIALECT-PHYSICS: NASHVILLE-POLISH}
[Dialect_Behavior: Controlled twang placement; Studio-precise articulation; Measured vibrato]
[Dialect_Phrasing: Radio-ready phrase lengths; Hook-conscious delivery]
[Dialect_Character: Music Row precision; Slight country accent retained; Professional warmth]`,
    sunoTags: ['nashville vocals', 'country polish', 'radio-ready voice'],
    excludeTags: ['raw', 'unpolished', 'lo-fi']
  },
  {
    id: 'delta-blues',
    label: 'Delta Blues',
    region: 'Mississippi Delta',
    description: 'Raw, field-holler inflection with moaning vowels and spoken-word bleed. The crossroads in every phrase.',
    traitModifiers: { grit: 15, rasp: 10, airiness: 5, lowEnd: 8, cleanness: -12, speed: -8 },
    physicsPrompt: `{DIALECT-PHYSICS: DELTA-BLUES}
[Dialect_Behavior: Field-holler moaning vowels; Spoken-sung boundary blur; Raw glottal scrape]
[Dialect_Phrasing: Conversational flow breaking into melody; Moan-to-sing transitions]
[Dialect_Character: Crossroads weight; Cotton-field endurance; Juke-joint intimacy]`,
    sunoTags: ['delta blues vocals', 'field holler', 'raw blues voice', 'mississippi blues'],
    excludeTags: ['polished', 'pop', 'bright', 'processed']
  },
  {
    id: 'new-orleans-brass',
    label: 'New Orleans Brass',
    region: 'New Orleans, LA',
    description: 'Parade-route swagger with brass-section phrasing. Second-line rhythmic bounce in the vocal delivery.',
    traitModifiers: { projection: 12, speed: 5, vibrato: 5, nasality: -3, lowEnd: 3 },
    physicsPrompt: `{DIALECT-PHYSICS: NEW-ORLEANS-BRASS}
[Dialect_Behavior: Brass-section phrasing; Second-line rhythmic bounce; Parade-route projection]
[Dialect_Phrasing: Syncopated delivery; Brass-punched accents; Mardi Gras swagger]
[Dialect_Character: Bourbon Street confidence; Horn-section breath control; Celebration energy]`,
    sunoTags: ['new orleans vocals', 'brass vocals', 'second line', 'nola swagger'],
    excludeTags: ['subdued', 'muted', 'introverted']
  },
  {
    id: 'ozark-mountain',
    label: 'Ozark Mountain',
    region: 'Ozarks',
    description: 'Isolated mountain dialect with archaic vowel sounds and hardscrabble consonant edges.',
    traitModifiers: { nasality: 10, grit: 10, rasp: 5, speed: -5, cleanness: -5 },
    physicsPrompt: `{DIALECT-PHYSICS: OZARK-MOUNTAIN}
[Dialect_Behavior: Archaic vowel formations; Hardscrabble consonant edges; Isolated dialect markers]
[Dialect_Phrasing: Short, declarative phrases; Mountain-hollow echo awareness]
[Dialect_Character: Ridge-runner resilience; Old-world vowel preservation; Holler-carved tone]`,
    sunoTags: ['ozark vocals', 'mountain voice', 'hillbilly twang'],
    excludeTags: ['cosmopolitan', 'urban', 'modern pop']
  },
  {
    id: 'gospel-church',
    label: 'Gospel Church',
    region: 'Southern Black Church',
    description: 'Pentecostal fire with call-and-response phrasing, spirit-filled runs, and congregational authority.',
    traitModifiers: { projection: 15, vibrato: 15, grit: 5, lowEnd: 5, airiness: -5 },
    physicsPrompt: `{DIALECT-PHYSICS: GOSPEL-CHURCH}
[Dialect_Behavior: Spirit-filled melismatic runs; Call-and-response breath patterns; Congregational authority]
[Dialect_Phrasing: Building intensity arcs; Testimony crescendos; Spirit-break moments]
[Dialect_Character: Pentecostal fire; Choir-trained power; Altar-call emotional weight]`,
    sunoTags: ['gospel vocals', 'church voice', 'spirit-filled', 'pentecostal power'],
    excludeTags: ['secular monotone', 'cold', 'restrained']
  },
  {
    id: 'honky-tonk-saloon',
    label: 'Honky-Tonk Saloon',
    region: 'Texas / Tennessee Honky-Tonks',
    description: 'Barroom drawl with whiskey-soaked edges. Every word sounds like it was sung through a cloud of smoke at 2 AM.',
    traitModifiers: { grit: 8, rasp: 10, airiness: 5, cleanness: -8, lowEnd: 3, vibrato: -3 },
    physicsPrompt: `{DIALECT-PHYSICS: HONKY-TONK-SALOON}
[Dialect_Behavior: Whiskey-soaked rasp; Smoke-filtered vowels; Barroom-volume calibration]
[Dialect_Phrasing: Story-song delivery; Conversational-to-singing transitions; Beer-glass pauses]
[Dialect_Character: 2AM barstool intimacy; Neon-lit warmth; Jukebox-distance mixing]`,
    sunoTags: ['honky tonk vocals', 'barroom voice', 'whiskey vocals', 'saloon singer'],
    excludeTags: ['pristine', 'studio-clean', 'digital']
  },
  {
    id: 'outlaw-road',
    label: 'Outlaw Road',
    region: 'The Highway',
    description: 'Road-worn rebel delivery with highway-mile rasp and defiant phrasing. Every word breaks a rule.',
    traitModifiers: { grit: 12, rasp: 8, projection: 8, cleanness: -10, speed: 3, nasality: 3 },
    physicsPrompt: `{DIALECT-PHYSICS: OUTLAW-ROAD}
[Dialect_Behavior: Road-worn rasp; Defiant consonant attacks; Highway-mile breath endurance]
[Dialect_Phrasing: Rule-breaking phrase lengths; Anti-establishment cadence; Rebel swagger]
[Dialect_Character: Open-road freedom; 18-wheeler rumble undertone; Wanted-poster authority]`,
    sunoTags: ['outlaw vocals', 'rebel voice', 'road-worn', 'outlaw country vocals'],
    excludeTags: ['compliant', 'safe', 'commercial pop']
  }
];

export function getDialectById(id: string): DialectModifier | undefined {
  return DIALECT_MODIFIERS.find(d => d.id === id);
}
